import { Alert, AlertTitle, Box, Button, Checkbox, Stack, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';

type Props = {
  isSalesAssistedAccount: boolean;
  seatsLeft: number;
  handleInviteUsers: () => void;
  isDisabled: boolean;
};

export const InvitationFooter = ({
  isSalesAssistedAccount,
  handleInviteUsers,
  seatsLeft,
  isDisabled,
}: Props) => {
  const translate = useTr();

  const [checkboxChecked, setCheckboxChecked] = useState(isSalesAssistedAccount);

  const handleCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    setCheckboxChecked(event.target.checked);
  };

  return (
    <>
      <FlexContainer direction="column" gap="three">
        <Alert severity="info">
          <Stack spacing={2}>
            <Box>
              <AlertTitle>
                <FormattedMessage id="profile.users.invite_user.info_message_roles_headline" />
              </AlertTitle>

              <Typography variant="body1">
                <FormattedMessage
                  id="profile.users.invite_user.info_message_roles_text"
                  values={{ br: <br /> }}
                />
              </Typography>
            </Box>
            <Box>
              <AlertTitle>
                <FormattedMessage id="profile.users.invite_user.info_message_billing_headline" />
              </AlertTitle>

              <Typography variant="body1">
                <FormattedMessage
                  id="profile.users.invite_user.info_message_billing_text"
                  values={{
                    a: (msg: string) => (
                      <StyledLink
                        href={translate('external_links.app_landingpage_pricing_plans')}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {msg}
                      </StyledLink>
                    ),
                  }}
                />
              </Typography>
            </Box>
          </Stack>
        </Alert>

        {!isSalesAssistedAccount ? (
          <CheckboxContainer>
            <StyledCheckbox onChange={handleCheckbox} checked={checkboxChecked} />

            <CheckboxText variant="body1">
              <FormattedMessage id="profile.users.invite_user.invite_checkbox_label" />
            </CheckboxText>
          </CheckboxContainer>
        ) : (
          <Alert severity="success">
            <Stack spacing={2}>
              <Box>
                <AlertTitle>
                  <FormattedMessage id="profile.users.invite_user.info_message_available_seats_headline" />
                </AlertTitle>

                <Typography variant="body1">
                  <FormattedMessage
                    id="profile.users.invite_user.info_message_available_seats_text"
                    values={{ seatsLeft }}
                  />
                </Typography>
              </Box>
            </Stack>
          </Alert>
        )}
      </FlexContainer>

      <Footer direction="row" alignItems="center" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleInviteUsers}
          disabled={isDisabled || !checkboxChecked}
        >
          <FormattedMessage id="profile.users.invite_user.invite_button_label" />
        </Button>
      </Footer>
    </>
  );
};

const Footer = styled(FlexContainer)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacings.medium};
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.black};
  text-decoration: underline;

  &:hover {
    color: inherit;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  flex: 0 0 ${({ theme }) => theme.spacings.two};
`;

const CheckboxText = styled(Typography)`
  flex: 1 1 auto;
`;

const CheckboxContainer = styled.div`
  margin: ${({ theme }) => theme.spacings.three} 0;
  display: flex;
  align-items: flex-start;
  gap: 5px;
`;
